import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  convertToCurrencyFormat,
  getSourceItemRemainder,
  getSourceItemRemainderTaxAmount,
  getSourceItemUnitLabel,
  isManagementUser,
} from "../../../../shared/utility";
import DeleteConfirmationForm from "../../DeleteConfirmationForm";
import OrderSourceItemPriceSplitModal from "../OrderSourceItemPriceSplitModal";
import {
  orderSubtableColumnWidths,
  orderSubtableSplitLabelColSpan,
} from "./orderSubtableLayout";

export default function OrderSourceItemSplitsTable(props) {
  const {
    sourceItem,
    setEmptyModelOpen,
    showErrorModal,
    onUpdatePriceSplit,
    onDeletePriceSplit,
    canEditSplits,
    showSplitsColumn,
    showEditorColumn,
  } = props;

  const isManagement = isManagementUser();
  const unitLabel = getSourceItemUnitLabel(sourceItem.selling_unit_of_measure);
  const isPieceUnit = parseInt(sourceItem.selling_unit_of_measure, 10) === 3;
  const splits = sourceItem.price_splits || [];
  const remainder = getSourceItemRemainder(sourceItem);
  const remainderTaxAmount = getSourceItemRemainderTaxAmount(sourceItem);

  const tableRows = [
    ...splits.map((split) => ({ ...split, rowType: "split" })),
    {
      id: `remainder-${sourceItem.id}`,
      rowType: "remainder",
      quantity: remainder.quantity,
      unit_price: remainder.unitPrice,
      subtotal_price: remainder.subtotalPrice,
      tax_amount: remainderTaxAmount,
    },
  ];

  const formatQty = (value) => Number(value ?? 0).toFixed(isPieceUnit ? 0 : 2);

  const openEditModal = (split) => {
    setEmptyModelOpen({
      childComponent: (
        <OrderSourceItemPriceSplitModal
          sourceItem={sourceItem}
          split={split}
          setOpen={setEmptyModelOpen}
          showErrorModal={showErrorModal}
          onConfirm={(payload) =>
            onUpdatePriceSplit(sourceItem.id, split.id, payload)
          }
        />
      ),
      open: true,
    });
  };

  const openDeleteModal = (split) => {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          title="Delete price split"
          buttonMessage="Delete"
          hideReason={true}
          confirmAction={async () => {
            const result = await onDeletePriceSplit(sourceItem.id, split.id);
            if (result?.success) {
              setEmptyModelOpen({ open: false, childComponent: null });
            }
          }}
          message="Are you sure you want to delete this price split?"
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  return (
    <div className="shipping-sub-items-splits-panel">
      <table className="shipping-sub-items-splits-table">
        <colgroup>
          <col style={{ width: orderSubtableColumnWidths.product }} />
          <col style={{ width: orderSubtableColumnWidths.lot }} />
          <col style={{ width: orderSubtableColumnWidths.serial }} />
          <col style={{ width: orderSubtableColumnWidths.weight }} />
          <col style={{ width: orderSubtableColumnWidths.pieces }} />
          {isManagement ? (
            <>
              <col style={{ width: orderSubtableColumnWidths.unitPrice }} />
              <col style={{ width: orderSubtableColumnWidths.subtotal }} />
              <col style={{ width: orderSubtableColumnWidths.tax }} />
              <col style={{ width: orderSubtableColumnWidths.taxRates }} />
            </>
          ) : null}
          <col style={{ width: orderSubtableColumnWidths.unitType }} />
          {showSplitsColumn ? (
            <col style={{ width: orderSubtableColumnWidths.splits }} />
          ) : null}
          {showEditorColumn ? (
            <col style={{ width: orderSubtableColumnWidths.editor }} />
          ) : null}
        </colgroup>
        <tbody>
          {tableRows.map((row) => {
            const isRemainder = row.rowType === "remainder";
            const label = isRemainder ? "Remainder" : "Split";
            const labelClass = isRemainder
              ? "font-medium text-gray-700"
              : "text-gray-500";

            return (
              <tr key={row.id} className="shipping-sub-items-splits-row">
                <td
                  colSpan={orderSubtableSplitLabelColSpan}
                  className={`pl-6 ${labelClass}`}
                >
                  {label}
                </td>
                <td className="text-right">
                  {formatQty(row.quantity)} {unitLabel}
                </td>
                {isManagement ? (
                  <>
                    <td className="text-right">
                      {convertToCurrencyFormat(row.unit_price, 2)}
                    </td>
                    <td className="text-right">
                      {convertToCurrencyFormat(row.subtotal_price, 2)}
                    </td>
                    <td className="text-right">
                      {convertToCurrencyFormat(row.tax_amount ?? 0, 2)}
                    </td>
                    <td />
                  </>
                ) : null}
                <td />
                {showSplitsColumn ? (
                  <td className="text-center">
                    {!isRemainder && canEditSplits ? (
                      <div className="inline-flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => openEditModal(row)}
                          className="inline-flex items-center justify-center rounded p-1 text-main-purple hover:bg-indigo-50 hover:text-indigo-700"
                          title="Edit split"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openDeleteModal(row)}
                          className="inline-flex items-center justify-center rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-700"
                          title="Delete split"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ) : null}
                  </td>
                ) : null}
                {showEditorColumn ? <td /> : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
